/**
 * PadLab's control vocabulary, and the shared primitives the rest of the component system
 * is built on.
 *
 * Nothing here is a styled `<input type="range">` or a stock checkbox. A performance
 * instrument that borrows the browser's controls stops looking like an instrument the
 * moment the user drags one — the thumb animates on its own schedule, the track repaints
 * at a different rate to the meters beside it, and on iOS the whole thing grows a system
 * accent colour we never chose. Every control below is built from a `<button>` or a
 * `role`-carrying element plus Pointer Events, which is also what lets a knob report
 * "1.20 kHz" to VoiceOver instead of "0.43".
 *
 * The descriptor-driven controls (`Knob`, `ParameterRow`) take an `EffectParameter` rather
 * than a numeric range, because the descriptor table in the model already owns the range,
 * the taper, the unit and the display name that the DSP layer uses. Passing the descriptor
 * is what keeps a dial from ever disagreeing with the audio graph.
 */

import { effect, isSignal, signal } from '../../core/signal';
import type { ReadonlySignal } from '../../core/signal';
import { disposeTree, h, on, onCleanup, schedule, svg } from '../../core/dom';
import type { Child } from '../../core/dom';
import { createLogger, tag } from '../../core/log';
import {
  clamp,
  effectParameterClamp,
  effectParameterDenormalize,
  effectParameterFormat,
  effectParameterNormalize,
} from '../../model/types';
import type { EffectParameter } from '../../model/types';

const log = createLogger('ui');

// MARK: - Reactive plumbing

/** A prop that may be a fixed value or a signal the component should follow. */
export type Reactive<T> = T | ReadonlySignal<T>;

/**
 * Applies `source` now and on every later change, coalescing changes into one animation
 * frame. The first application is synchronous so a control is never blank for a frame.
 *
 * The write is registered as a cleanup on `node`, so `disposeTree` (which `mount` and the
 * keyed `list` helper both call) releases the subscription without any bookkeeping at the
 * call site.
 */
export function bindReactive<T>(node: Node, source: Reactive<T>, apply: (value: T) => void): void {
  if (!isSignal(source)) {
    apply(source as T);
    return;
  }
  const source_ = source as ReadonlySignal<T>;
  let latest!: T;
  let primed = false;
  let cancel: (() => void) | null = null;
  const write = (): void => {
    cancel = null;
    apply(latest);
  };
  const dispose = effect(() => {
    latest = source_.value;
    if (!primed) {
      primed = true;
      apply(latest);
      return;
    }
    if (cancel === null) cancel = schedule(write);
  });
  onCleanup(node, () => {
    dispose();
    cancel?.();
    cancel = null;
  });
}

/** Reads a `Reactive` once, without subscribing. */
export function peekReactive<T>(source: Reactive<T>): T {
  return isSignal(source) ? (source as ReadonlySignal<T>).peek() : (source as T);
}

// MARK: - Accessibility preferences

function mediaSignal(query: string): ReadonlySignal<boolean> {
  const state = signal(false);
  if (typeof matchMedia !== 'function') return state;
  const list = matchMedia(query);
  state.value = list.matches;
  const listener = (event: MediaQueryListEvent): void => {
    state.value = event.matches;
  };
  // Safari only gained `addEventListener` on MediaQueryList in 14; the deprecated
  // `addListener` is the difference between honouring Reduce Motion and ignoring it on an
  // older iPhone that is otherwise perfectly capable of running the app.
  if (typeof list.addEventListener === 'function') list.addEventListener('change', listener);
  else if (typeof list.addListener === 'function') list.addListener(listener);
  return state;
}

/** True while the user has asked for reduced motion. Live — components may bind to it. */
export const reducedMotion: ReadonlySignal<boolean> = mediaSignal('(prefers-reduced-motion: reduce)');

export function prefersReducedMotion(): boolean {
  return reducedMotion.peek();
}

// MARK: - Theme tokens
//
// Meters and waveforms redraw at frame rate, and `getComputedStyle` forces a style
// resolution. Resolving `--hairline` twenty-six times per frame — once per pad meter, once
// per channel of the stereo meter, once for the waveform — is a measurable cost for values
// that only change when an accessibility preference changes. So they are read once and
// cached, and the cache is dropped when a preference that rewrites them flips.

const tokenCache = new Map<string, string>();

function invalidateTokens(): void {
  tokenCache.clear();
}

for (const query of ['(prefers-contrast: more)', '(prefers-reduced-transparency: reduce)']) {
  if (typeof matchMedia !== 'function') break;
  const list = matchMedia(query);
  if (typeof list.addEventListener === 'function') list.addEventListener('change', invalidateTokens);
  else if (typeof list.addListener === 'function') list.addListener(invalidateTokens);
}

/** Current computed value of a design token, cached. `--hairline` → `#24242c`. */
export function themeToken(name: string, fallback: string): string {
  const cached = tokenCache.get(name);
  if (cached !== undefined) return cached;
  const resolved =
    typeof getComputedStyle === 'function'
      ? getComputedStyle(document.documentElement).getPropertyValue(name).trim()
      : '';
  const value = resolved === '' ? fallback : resolved;
  tokenCache.set(name, value);
  return value;
}

/** Turns a CSS colour that may be `var(--accent-magenta)` into something canvas can use. */
export function resolveColour(value: string | undefined, fallback: string): string {
  if (value === undefined || value === '') return fallback;
  const trimmed = value.trim();
  const match = /^var\(\s*(--[\w-]+)\s*(?:,\s*(.*))?\)$/.exec(trimmed);
  if (match === null) return trimmed;
  const name = match[1];
  if (name === undefined) return fallback;
  return themeToken(name, match[2]?.trim() ?? fallback);
}

/** `#RRGGBB` (or any resolved colour) plus an alpha, for canvas fills that need to sit back. */
export function withAlpha(colour: string, alpha: number): string {
  const hex = /^#([0-9a-f]{6})$/i.exec(colour.trim());
  if (hex !== null && hex[1] !== undefined) {
    const value = Number.parseInt(hex[1], 16);
    return `rgba(${(value >> 16) & 0xff}, ${(value >> 8) & 0xff}, ${value & 0xff}, ${alpha})`;
  }
  const short = /^#([0-9a-f]{3})$/i.exec(colour.trim());
  if (short !== null && short[1] !== undefined) {
    const digits = short[1];
    const r = Number.parseInt(`${digits[0]}${digits[0]}`, 16);
    const g = Number.parseInt(`${digits[1]}${digits[1]}`, 16);
    const b = Number.parseInt(`${digits[2]}${digits[2]}`, 16);
    return `rgba(${r}, ${g}, ${b}, ${alpha})`;
  }
  // A named or functional colour we cannot decompose still has to honour the alpha, and
  // `color-mix` is the one way to do that without parsing every CSS colour syntax.
  return `color-mix(in srgb, ${colour} ${Math.round(alpha * 100)}%, transparent)`;
}

// MARK: - Size observation
//
// One ResizeObserver for the whole component system. A board with twenty-four pad meters,
// a stereo meter and a waveform would otherwise allocate twenty-six observers, each with
// its own delivery task, for information that arrives on the same frame anyway.

type SizeCallback = () => void;

const sizeCallbacks = new WeakMap<Element, Set<SizeCallback>>();
let sharedResizeObserver: ResizeObserver | null = null;
let windowResizeHandler: (() => void) | null = null;
const windowResizeTargets = new Set<Element>();

function ensureResizeObserver(): ResizeObserver | null {
  if (typeof ResizeObserver !== 'function') return null;
  sharedResizeObserver ??= new ResizeObserver((entries) => {
    for (const entry of entries) {
      const callbacks = sizeCallbacks.get(entry.target);
      if (callbacks === undefined) continue;
      for (const callback of [...callbacks]) callback();
    }
  });
  return sharedResizeObserver;
}

/** Calls `fn` whenever `element`'s border box changes. Returns an unsubscribe function. */
export function observeSize(element: Element, fn: SizeCallback): () => void {
  let callbacks = sizeCallbacks.get(element);
  if (callbacks === undefined) {
    callbacks = new Set();
    sizeCallbacks.set(element, callbacks);
  }
  callbacks.add(fn);

  const observer = ensureResizeObserver();
  if (observer !== null) {
    observer.observe(element);
  } else {
    // No ResizeObserver: a window resize is the only signal left, and it covers rotation
    // and the split-view drag, which are the cases that actually matter on a phone.
    windowResizeTargets.add(element);
    if (windowResizeHandler === null) {
      windowResizeHandler = () => {
        for (const target of windowResizeTargets) {
          const list = sizeCallbacks.get(target);
          if (list === undefined) continue;
          for (const callback of [...list]) callback();
        }
      };
      window.addEventListener('resize', windowResizeHandler);
      window.addEventListener('orientationchange', windowResizeHandler);
    }
  }

  return () => {
    const list = sizeCallbacks.get(element);
    if (list === undefined) return;
    list.delete(fn);
    if (list.size > 0) return;
    sizeCallbacks.delete(element);
    observer?.unobserve(element);
    windowResizeTargets.delete(element);
  };
}

// MARK: - Canvas surfaces

export interface CanvasSurface {
  readonly canvas: HTMLCanvasElement;
  readonly context: CanvasRenderingContext2D;
  /** CSS pixels. The context is already scaled, so draw in these units. */
  readonly width: number;
  readonly height: number;
  /** Re-reads the element size and rescales the backing store. Returns true if it changed. */
  measure(): boolean;
  dispose(): void;
}

/**
 * A canvas whose backing store tracks `devicePixelRatio`, so a waveform stroke is one
 * device pixel rather than a blurry three on a Retina screen.
 *
 * The ratio is re-read on every measure rather than captured once: dragging a window
 * between a Retina and a non-Retina display, and iPadOS's Display Zoom, both change it
 * while the page is live.
 */
export function createCanvasSurface(
  className: string,
  onDraw: () => void,
  maxRatio = 3,
): CanvasSurface {
  const canvas = h('canvas', { class: className, 'aria-hidden': 'true' });
  const context = canvas.getContext('2d', { alpha: true });
  if (context === null) {
    // A browser that cannot give us a 2D context cannot draw a meter or a waveform. The
    // surrounding component still renders its labels and stays operable; only the drawing
    // is missing, which is why this warns rather than throws.
    log.warn('2D canvas context unavailable', { surface: tag(className) });
  }

  let width = 0;
  let height = 0;
  let ratio = 0;

  const surface: CanvasSurface = {
    canvas,
    context: context ?? ({} as CanvasRenderingContext2D),
    get width() {
      return width;
    },
    get height() {
      return height;
    },
    measure(): boolean {
      const rect = canvas.getBoundingClientRect();
      const nextRatio = Math.min(maxRatio, Math.max(1, window.devicePixelRatio || 1));
      const nextWidth = Math.max(0, rect.width);
      const nextHeight = Math.max(0, rect.height);
      if (nextWidth === width && nextHeight === height && nextRatio === ratio) return false;
      width = nextWidth;
      height = nextHeight;
      ratio = nextRatio;
      // Ceil, not round: a flex layout routinely hands out fractional widths, and rounding
      // down leaves a sub-pixel column of the canvas with no backing store behind it —
      // visible as a hairline gap at the right edge of a meter.
      canvas.width = Math.max(1, Math.ceil(width * ratio));
      canvas.height = Math.max(1, Math.ceil(height * ratio));
      context?.setTransform(ratio, 0, 0, ratio, 0, 0);
      return true;
    },
    dispose(): void {
      unobserve();
    },
  };

  const unobserve = observeSize(canvas, () => {
    if (surface.measure()) onDraw();
  });
  onCleanup(canvas, unobserve);

  return surface;
}

// MARK: - Icons
//
// SF Symbol names are the shared vocabulary between the two builds: `Pad.symbolName` and
// `PadState.glyphName` are serialised SF names, so a project authored on the phone names
// its glyphs that way and the web has to answer to the same names. These are not traced
// copies of Apple's artwork — they are PadLab's own geometry on a 24-unit grid, drawn in
// one line weight so a badge, a button and a banner glyph read as the same family.
//
// `.fill` in a name is honoured as *identity*, not as a literal instruction to flood the
// shape: a filled triangle with a knocked-out exclamation mark needs to know the colour
// behind it, and at a 10 px badge size a stroked outline is the more legible of the two.
// The few silhouettes that must be solid to read at speed — play, stop, pause, the record
// dot — are solid.

type IconPart =
  | readonly ['p', string]
  | readonly ['f', string]
  | readonly ['c', number, number, number]
  | readonly ['fc', number, number, number]
  | readonly ['fr', number, number, number, number, number];

const ICONS: Readonly<Record<string, readonly IconPart[]>> = {
  plus: [['p', 'M12 5V19M5 12H19']],
  minus: [['p', 'M5 12H19']],
  xmark: [['p', 'M6.2 6.2 17.8 17.8M17.8 6.2 6.2 17.8']],
  checkmark: [['p', 'M4.8 12.4 10 17.6 19.2 6.9']],
  'play.fill': [['f', 'M8 5.1 19 12 8 18.9Z']],
  'pause.fill': [
    ['fr', 8, 5, 3, 14, 1.2],
    ['fr', 13, 5, 3, 14, 1.2],
  ],
  'stop.fill': [['fr', 6, 6, 12, 12, 2]],
  'record.circle.fill': [
    ['c', 12, 12, 8.6],
    ['fc', 12, 12, 4.6],
  ],
  repeat: [
    ['p', 'M17 4.4 20.2 7.6 17 10.8'],
    ['p', 'M20.2 7.6H8.4A4.4 4.4 0 0 0 4 12v0.6'],
    ['p', 'M7 19.6 3.8 16.4 7 13.2'],
    ['p', 'M3.8 16.4h11.8A4.4 4.4 0 0 0 20 12v-0.6'],
  ],
  'clock.fill': [
    ['c', 12, 12, 8.4],
    ['p', 'M12 7.1V12l3.4 2.1'],
  ],
  'speaker.slash.fill': [
    ['f', 'M3.6 9.4h3L11.2 5.4v13.2L6.6 14.6h-3Z'],
    ['p', 'M15 8.6 20.6 15.4M20.6 8.6 15 15.4'],
  ],
  headphones: [
    ['p', 'M4.6 14.2v-2.6a7.4 7.4 0 0 1 14.8 0v2.6'],
    ['fr', 2.2, 12.4, 4.6, 7.8, 2.3],
    ['fr', 17.2, 12.4, 4.6, 7.8, 2.3],
  ],
  hourglass: [['p', 'M7 4h10M7 20h10M8.2 4v3.1L12 12l3.8-4.9V4M8.2 20v-3.1L12 12l3.8 4.9V20']],
  'questionmark.folder.fill': [
    ['p', 'M3 7.2a1.6 1.6 0 0 1 1.6-1.6h4.2l2 2.2h7.6A1.6 1.6 0 0 1 20 9.4v8.2a1.6 1.6 0 0 1-1.6 1.6H4.6A1.6 1.6 0 0 1 3 17.6Z'],
    ['p', 'M10 11.6a1.6 1.6 0 1 1 2 1.5v1'],
    ['fc', 12, 16.4, 0.85],
  ],
  'exclamationmark.triangle.fill': [
    ['p', 'M12 3.9 21.4 19.8H2.6Z'],
    ['p', 'M12 10v3.6'],
    ['fc', 12, 16.7, 0.95],
  ],
  exclamationmark: [
    ['p', 'M12 4.6v9.2'],
    ['fc', 12, 18, 1.15],
  ],
  'xmark.octagon.fill': [
    ['p', 'M8.6 3h6.8L21 8.6v6.8L15.4 21H8.6L3 15.4V8.6Z'],
    ['p', 'M9.6 9.6 14.4 14.4M14.4 9.6 9.6 14.4'],
  ],
  'checkmark.circle.fill': [
    ['c', 12, 12, 8.6],
    ['p', 'M8 12.3 11 15.3 16.2 9.3'],
  ],
  'info.circle.fill': [
    ['c', 12, 12, 8.6],
    ['p', 'M12 11.2v5.4'],
    ['fc', 12, 7.9, 1],
  ],
  'trash.fill': [
    ['p', 'M4.6 6.6h14.8'],
    ['p', 'M9.5 6.6V4.9a1 1 0 0 1 1-1h3a1 1 0 0 1 1 1v1.7'],
    ['p', 'M6.6 6.6 7.6 19.5a1.4 1.4 0 0 0 1.4 1.3h6a1.4 1.4 0 0 0 1.4-1.3l1-12.9'],
    ['p', 'M10.4 10v7M13.6 10v7'],
  ],
  waveform: [['p', 'M4 10v4M8 6.4v11.2M12 3.4v17.2M16 7.4v9.2M20 10v4']],
  'square.and.arrow.up': [
    ['p', 'M12 3.6v10.9'],
    ['p', 'M8.2 7.4 12 3.6l3.8 3.8'],
    ['p', 'M7.6 11.6H5.4a1.6 1.6 0 0 0-1.6 1.6v5.6a1.6 1.6 0 0 0 1.6 1.6h13.2a1.6 1.6 0 0 0 1.6-1.6v-5.6a1.6 1.6 0 0 0-1.6-1.6h-2.2'],
  ],
  'arrow.counterclockwise': [
    ['p', 'M4.1 12a7.9 7.9 0 1 0 2.5-5.7'],
    ['p', 'M3.4 3.9v4.4h4.4'],
  ],
  // The mirror of the one above. Without it, Redo drew the missing-glyph ring.
  'arrow.clockwise': [
    ['p', 'M19.9 12a7.9 7.9 0 1 1-2.5-5.7'],
    ['p', 'M20.6 3.9v4.4h-4.4'],
  ],
  magnifyingglass: [
    ['c', 10.8, 10.8, 6.3],
    ['p', 'M15.4 15.4 20.6 20.6'],
  ],
  'chevron.down': [['p', 'M6 9.4 12 15.4 18 9.4']],
  'chevron.up': [['p', 'M6 14.6 12 8.6 18 14.6']],
  'chevron.left': [['p', 'M14.6 5.4 8.6 12l6 6.6']],
  'chevron.right': [['p', 'M9.4 5.4 15.4 12l-6 6.6']],
  ellipsis: [
    ['fc', 5.8, 12, 1.5],
    ['fc', 12, 12, 1.5],
    ['fc', 18.2, 12, 1.5],
  ],
  'star.fill': [['f', 'M12 3.4 14.7 9.1 21 10 16.5 14.4 17.6 20.7 12 17.7 6.4 20.7 7.5 14.4 3 10l6.3-0.9Z']],
  'folder.fill': [
    ['p', 'M3 7.2a1.6 1.6 0 0 1 1.6-1.6h4.2l2 2.2h7.6A1.6 1.6 0 0 1 20 9.4v8.2a1.6 1.6 0 0 1-1.6 1.6H4.6A1.6 1.6 0 0 1 3 17.6Z'],
  ],

  // Effect kinds — the 24 `effectKindSymbolName` values.
  'slider.horizontal.3': [
    ['p', 'M3 7h3.6M11.4 7H21M3 12h6.6M14.4 12H21M3 17h5.6M13.4 17H21'],
    ['c', 9, 7, 2.2],
    ['c', 12, 12, 2.2],
    ['c', 11, 17, 2.2],
  ],
  'waveform.path': [['p', 'M2.6 12h2.4l2-6.2 3 12.4 3-9.4 2.4 6.2 2-3.2h3.6']],
  'waveform.path.badge.plus': [
    ['p', 'M2.6 10.4h2l1.8-5.4 2.6 10.6 2.4-7.6 1.8 4.6h1.6'],
    ['p', 'M17.4 17.6h5M19.9 15.1v5'],
  ],
  'waveform.path.badge.minus': [
    ['p', 'M2.6 10.4h2l1.8-5.4 2.6 10.6 2.4-7.6 1.8 4.6h1.6'],
    ['p', 'M17.4 17.6h5'],
  ],
  'arrow.down.right.and.arrow.up.left': [
    ['p', 'M3.6 3.6 9.4 9.4'],
    ['p', 'M9.4 5.2v4.2H5.2'],
    ['p', 'M20.4 20.4 14.6 14.6'],
    ['p', 'M14.6 18.8v-4.2h4.2'],
  ],
  'arrow.up.to.line.compact': [
    ['p', 'M4 4.4h16'],
    ['p', 'M12 20V8.2'],
    ['p', 'M7.4 12.8 12 8.2l4.6 4.6'],
  ],
  'door.left.hand.closed': [
    ['p', 'M5 3.6h14v16.8H5Z'],
    ['fc', 15.6, 12, 1.15],
  ],
  'flame.fill': [
    ['f', 'M12 2.8c3.4 3.6 5.4 6.3 5.4 9.4a5.4 5.4 0 0 1-10.8 0c0-1.9 1-3.5 2.4-4.9.2 1.7 1 2.5 1.9 2.7-.5-2.7.1-5.2 1.1-7.2Z'],
  ],
  'square.grid.3x3.fill': [
    ['fr', 3.6, 3.6, 4.2, 4.2, 1.1],
    ['fr', 9.9, 3.6, 4.2, 4.2, 1.1],
    ['fr', 16.2, 3.6, 4.2, 4.2, 1.1],
    ['fr', 3.6, 9.9, 4.2, 4.2, 1.1],
    ['fr', 9.9, 9.9, 4.2, 4.2, 1.1],
    ['fr', 16.2, 9.9, 4.2, 4.2, 1.1],
    ['fr', 3.6, 16.2, 4.2, 4.2, 1.1],
    ['fr', 9.9, 16.2, 4.2, 4.2, 1.1],
    ['fr', 16.2, 16.2, 4.2, 4.2, 1.1],
  ],
  'chart.bar.fill': [
    ['fr', 3.8, 13, 3.6, 7.2, 1],
    ['fr', 10.2, 8.4, 3.6, 11.8, 1],
    ['fr', 16.6, 4.2, 3.6, 16, 1],
  ],
  'circle.circle.fill': [
    ['c', 12, 12, 8.6],
    ['fc', 12, 12, 3.6],
  ],
  'aqi.medium': [
    ['p', 'M3 8.6q3-4 6 0t6 0 6 0'],
    ['p', 'M3 15.4q3-4 6 0t6 0 6 0'],
  ],
  'arrow.trianglehead.2.clockwise': [
    ['p', 'M19.6 8.4A8 8 0 1 0 20 12'],
    ['p', 'M15.4 8.4h4.4V4'],
  ],
  'person.3.fill': [
    ['c', 6.8, 9, 2.3],
    ['c', 12, 8, 2.7],
    ['c', 17.2, 9, 2.3],
    ['p', 'M3.2 17.6a3.8 3.8 0 0 1 4-3.2'],
    ['p', 'M7.8 18.8a4.4 4.4 0 0 1 8.4 0'],
    ['p', 'M16.8 14.4a3.8 3.8 0 0 1 4 3.2'],
  ],
  wind: [
    ['p', 'M3 8.4h10.4a2.6 2.6 0 1 0-2.6-2.6'],
    ['p', 'M3 12.6h13.2a2.8 2.8 0 1 1-2.8 2.8'],
    ['p', 'M3 16.8h6.4'],
  ],
  'waveform.badge.magnifyingglass': [
    ['p', 'M3.4 12h1.4M7.2 7.6v8.8M11 5v14M14.8 9v6'],
    ['c', 17.8, 17, 3.2],
    ['p', 'M20.2 19.4 22.4 21.6'],
  ],
  'arrow.up.arrow.down': [
    ['p', 'M7.4 20V4.6'],
    ['p', 'M3.9 8.1 7.4 4.6l3.5 3.5'],
    ['p', 'M16.6 4v15.4'],
    ['p', 'M13.1 15.9 16.6 19.4l3.5-3.5'],
  ],
  'mouth.fill': [
    ['p', 'M3.4 12q8.6-7 17.2 0-8.6 7-17.2 0Z'],
    ['p', 'M3.4 12h17.2'],
  ],
  'arrow.left.and.right': [
    ['p', 'M3 12h18'],
    ['p', 'M6.8 8.2 3 12l3.8 3.8'],
    ['p', 'M17.2 8.2 21 12l-3.8 3.8'],
  ],
  'music.note.list': [
    ['p', 'M3 7.4h4M3 12h4M3 16.6h3'],
    ['p', 'M12.6 17.6V7.4L20.6 5.7v10'],
    ['fc', 10.8, 17.8, 1.9],
    ['fc', 18.8, 16.1, 1.9],
  ],
  tuningfork: [
    ['p', 'M8 3.4V11a4 4 0 0 0 8 0V3.4'],
    ['p', 'M12 15v5.6M9.4 20.6h5.2'],
  ],
  'rectangle.split.3x1.fill': [
    ['p', 'M3.4 7h17.2v10H3.4Z'],
    ['p', 'M9.1 7v10M14.9 7v10'],
  ],
  'bolt.horizontal.fill': [['f', 'M2.4 13.6 11.6 5.8 9.8 11h11.8l-9.2 7.8 1.8-5.2Z']],
  'dial.medium.fill': [
    ['c', 12, 12, 8.6],
    ['p', 'M12 12 16 8'],
    ['fc', 12, 12, 1.6],
  ],
};

export interface IconOptions {
  /** Rendered edge length in CSS pixels. */
  readonly size?: number;
  readonly class?: string;
  /** Overrides `currentColor`. */
  readonly color?: string;
}

/**
 * Builds an inline SVG for an SF Symbol name.
 *
 * Line weight is chosen from the render size rather than fixed in the viewBox, because a
 * 1.7-unit stroke that looks right on a 24 px button renders as two thirds of a pixel on a
 * 10 px pad badge and disappears. The formula holds the drawn weight at roughly 1.4 CSS px
 * across every size the app uses.
 */
export function icon(name: string, options: IconOptions = {}): SVGSVGElement {
  const size = options.size ?? 20;
  const parts = ICONS[name];
  const strokeWidth = Math.min(3.2, Math.max(1.15, 34 / size));

  const root = svg('svg', {
    viewBox: '0 0 24 24',
    width: String(size),
    height: String(size),
    fill: 'none',
    stroke: options.color ?? 'currentColor',
    'stroke-width': String(strokeWidth),
    'stroke-linecap': 'round',
    'stroke-linejoin': 'round',
    class: options.class === undefined ? 'pl-icon' : `pl-icon ${options.class}`,
    'aria-hidden': 'true',
    focusable: 'false',
  });

  if (parts === undefined) {
    // A missing glyph must not leave a hole where a control's meaning was. The placeholder
    // is a plain ring: visibly "unknown" without pretending to be a different symbol.
    log.warn('No glyph for symbol name', { symbol: tag(name) });
    root.appendChild(svg('circle', { cx: '12', cy: '12', r: '7.5', 'stroke-dasharray': '2 3' }));
    return root;
  }

  for (const part of parts) {
    switch (part[0]) {
      case 'p':
        root.appendChild(svg('path', { d: part[1] }));
        break;
      case 'f':
        root.appendChild(svg('path', { d: part[1], fill: 'currentColor', stroke: 'none' }));
        break;
      case 'c':
        root.appendChild(
          svg('circle', { cx: String(part[1]), cy: String(part[2]), r: String(part[3]) }),
        );
        break;
      case 'fc':
        root.appendChild(
          svg('circle', {
            cx: String(part[1]),
            cy: String(part[2]),
            r: String(part[3]),
            fill: 'currentColor',
            stroke: 'none',
          }),
        );
        break;
      case 'fr':
        root.appendChild(
          svg('rect', {
            x: String(part[1]),
            y: String(part[2]),
            width: String(part[3]),
            height: String(part[4]),
            rx: String(part[5]),
            fill: 'currentColor',
            stroke: 'none',
          }),
        );
        break;
    }
  }
  return root;
}

export function hasIcon(name: string): boolean {
  return Object.prototype.hasOwnProperty.call(ICONS, name);
}

// MARK: - Small structural pieces

/** The uppercase micro-label above a value — "PLAYS OVER", "TEMPO", "BANK". */
export function kickerLabel(
  text: Reactive<string>,
  options: { readonly symbol?: string; readonly class?: string } = {},
): HTMLElement {
  const element = h('span', {
    class: options.class === undefined ? 'kicker pl-kicker' : `kicker pl-kicker ${options.class}`,
  });
  if (options.symbol !== undefined) element.appendChild(icon(options.symbol, { size: 9 }));
  const label = h('span');
  bindReactive(label, text, (value) => {
    label.textContent = value;
  });
  element.appendChild(label);
  return element;
}

export function hairlineDivider(axis: 'horizontal' | 'vertical' = 'horizontal'): HTMLElement {
  return h('div', { class: `pl-rule pl-rule--${axis}`, role: 'presentation' });
}

// MARK: - Press feedback
//
// Buttons act on `click`, not on `pointerdown`. `touch-action: manipulation` in the reset
// already removes the double-tap wait, so a click is immediate, and acting on release is
// what lets a finger that landed on the wrong control slide off and cancel — which matters
// more on a transport bar than the handful of milliseconds it costs. Pads are the
// exception and are handled in `pad.ts`, where the trigger fires on `pointerdown`.

function pressFeedback(element: HTMLElement): void {
  const release = (): void => element.classList.remove('is-pressed');
  onCleanup(
    element,
    on(element, 'pointerdown', () => {
      if (element.matches(':disabled')) return;
      element.classList.add('is-pressed');
    }),
  );
  for (const type of ['pointerup', 'pointercancel', 'pointerleave', 'blur'] as const) {
    onCleanup(element, on(element, type, release));
  }
}

/**
 * Claims the pointer for `element` for the rest of the gesture.
 *
 * `setPointerCapture` throws `NotFoundError` when the id no longer belongs to an active
 * pointer — which happens for real when a finger is lifted between the event being queued
 * and the handler running, and every time in a synthesised-event test. Capture is an
 * improvement to the gesture, never a correctness requirement, so a failure is swallowed
 * rather than allowed to abort a handler that has already told the engine to play.
 */
export function capturePointer(element: Element, pointerId: number): void {
  try {
    element.setPointerCapture(pointerId);
  } catch {
    // The pointer is already gone; the element still receives the remaining events.
  }
}

/** Every control returns this shape, so a screen can tear one down without knowing which it is. */
export interface ControlHandle<E extends HTMLElement = HTMLElement> {
  readonly el: E;
  setEnabled(enabled: boolean): void;
  destroy(): void;
}

function baseHandle<E extends HTMLElement>(el: E, setEnabled: (on: boolean) => void): ControlHandle<E> {
  return {
    el,
    setEnabled,
    destroy(): void {
      disposeTree(el);
      el.remove();
    },
  };
}

function setDisabled(element: HTMLElement, disabled: boolean): void {
  if (element instanceof HTMLButtonElement) {
    element.disabled = disabled;
    return;
  }
  element.classList.toggle('is-disabled', disabled);
  if (disabled) {
    element.setAttribute('aria-disabled', 'true');
    element.setAttribute('tabindex', '-1');
  } else {
    element.removeAttribute('aria-disabled');
    element.setAttribute('tabindex', '0');
  }
}

// MARK: - NeonButton

export type NeonButtonStyle = 'primary' | 'secondary' | 'destructive' | 'ghost';

export interface NeonButtonOptions {
  readonly title: Reactive<string>;
  readonly symbol?: string;
  readonly style?: NeonButtonStyle;
  readonly fullWidth?: boolean;
  readonly enabled?: Reactive<boolean>;
  readonly type?: 'button' | 'submit';
  readonly onPress: () => void;
}

export interface NeonButtonHandle extends ControlHandle<HTMLButtonElement> {
  setTitle(title: string): void;
}

export function neonButton(options: NeonButtonOptions): NeonButtonHandle {
  const style = options.style ?? 'primary';
  const label = h('span', { class: 'pl-btn__label' });

  const button = h(
    'button',
    {
      type: options.type ?? 'button',
      class: [
        'pl-btn',
        `pl-btn--${style}`,
        options.fullWidth === true ? 'pl-btn--full' : '',
      ],
      onClick: () => options.onPress(),
    },
    options.symbol === undefined ? null : icon(options.symbol, { size: 15, class: 'pl-btn__icon' }),
    label,
  );

  bindReactive(label, options.title, (value) => {
    label.textContent = value;
  });
  if (options.enabled !== undefined) {
    bindReactive(button, options.enabled, (value) => setDisabled(button, !value));
  }
  pressFeedback(button);

  return {
    ...baseHandle(button, (enabled) => setDisabled(button, !enabled)),
    setTitle(title: string): void {
      label.textContent = title;
    },
  };
}

// MARK: - IconButton

export interface IconButtonOptions {
  readonly symbol: string;
  /**
   * Required: a symbol-only button with a guessed label is worse than no button at all.
   *
   * Reactive, because a control whose meaning changes has to say so — "Undo Clear Pad" and
   * "Edit pads, on" are the name of the button, not decoration around it.
   */
  readonly label: Reactive<string>;
  /** Spoken instead of `label` while `active` is true. Toggles whose verb flips need it. */
  readonly activeLabel?: string;
  readonly active?: Reactive<boolean>;
  readonly enabled?: Reactive<boolean>;
  readonly size?: number;
  readonly onPress: () => void;
}

export interface IconButtonHandle extends ControlHandle<HTMLButtonElement> {
  setActive(active: boolean): void;
}

export function iconButton(options: IconButtonOptions): IconButtonHandle {
  const size = Math.max(44, options.size ?? 44);
  const button = h(
    'button',
    {
      type: 'button',
      class: 'pl-iconbtn',
      'aria-label': peekReactive(options.label),
      'aria-pressed': 'false',
      // No per-instance tint: an icon button that is on is magenta like every other active
      // element in the app, so a toolbar can never become a row of different colours.
      style: { '--btn-size': `${size}px` },
      onClick: () => options.onPress(),
    },
    icon(options.symbol, { size: Math.round(size * 0.38) }),
  );

  let restingLabel = peekReactive(options.label);
  let isActive = false;
  const writeLabel = (): void => {
    button.setAttribute('aria-label', isActive ? options.activeLabel ?? restingLabel : restingLabel);
  };
  bindReactive(button, options.label, (value) => {
    restingLabel = value;
    writeLabel();
  });

  const setActive = (active: boolean): void => {
    isActive = active;
    button.classList.toggle('is-active', active);
    button.setAttribute('aria-pressed', active ? 'true' : 'false');
    writeLabel();
  };

  if (options.active !== undefined) bindReactive(button, options.active, setActive);
  if (options.enabled !== undefined) {
    bindReactive(button, options.enabled, (value) => setDisabled(button, !value));
  }
  pressFeedback(button);

  return { ...baseHandle(button, (enabled) => setDisabled(button, !enabled)), setActive };
}

// MARK: - TransportButton

export type TransportKind = 'play' | 'stop' | 'record' | 'panic';

export interface TransportButtonOptions {
  readonly kind: TransportKind;
  readonly active?: Reactive<boolean>;
  readonly enabled?: Reactive<boolean>;
  readonly onPress: () => void;
}

export interface TransportButtonHandle extends ControlHandle<HTMLButtonElement> {
  setActive(active: boolean): void;
}

const TRANSPORT_LABEL: Readonly<Record<TransportKind, readonly [string, string]>> = {
  // [resting label, active label]
  play: ['Play', 'Pause'],
  stop: ['Stop', 'Stop'],
  // The record control *is* the stop control while a take is running. A button called
  // "Record" that ends the recording is the worst thing on the core path for a user who
  // cannot see the red dot.
  record: ['Record', 'Stop recording'],
  panic: ['Panic, stop all sound', 'Panic, stop all sound'],
};

const TRANSPORT_VALUE: Readonly<Record<TransportKind, readonly [string, string]>> = {
  play: ['Stopped', 'Playing'],
  stop: ['', ''],
  record: ['Off', 'Armed'],
  panic: ['', ''],
};

export function transportButton(options: TransportButtonOptions): TransportButtonHandle {
  const { kind } = options;
  const glyphSize = kind === 'record' ? 26 : kind === 'panic' ? 24 : 22;
  const glyph = h('span', { class: 'pl-transport__glyph' });
  glyph.appendChild(icon(transportSymbol(kind, false), { size: glyphSize }));

  // `title` is a tooltip: unreachable on touch, and VoiceOver does not announce it on a
  // button that already has an `aria-label`. The state goes in a visually-hidden span the
  // button describes itself with — the same treatment `pad.ts` gives pad state — and `title`
  // is kept for pointer users only.
  const hasSpokenState = TRANSPORT_VALUE[kind][0] !== '' || TRANSPORT_VALUE[kind][1] !== '';
  const stateId = `pl-transport-state-${nextId()}`;
  const spokenState = h('span', { class: 'visually-hidden', id: stateId }, TRANSPORT_VALUE[kind][0]);

  const button = h(
    'button',
    {
      type: 'button',
      class: `pl-transport pl-transport--${kind}`,
      'aria-label': TRANSPORT_LABEL[kind][0],
      'aria-pressed': kind === 'stop' || kind === 'panic' ? null : 'false',
      'aria-describedby': hasSpokenState ? stateId : null,
      onClick: () => options.onPress(),
    },
    glyph,
    hasSpokenState ? spokenState : null,
  );

  let currentActive = false;
  const setActive = (active: boolean): void => {
    if (active === currentActive) return;
    currentActive = active;
    button.classList.toggle('is-active', active);
    button.setAttribute('aria-label', TRANSPORT_LABEL[kind][active ? 1 : 0]);
    if (kind !== 'stop' && kind !== 'panic') {
      button.setAttribute('aria-pressed', active ? 'true' : 'false');
    }
    const spoken = TRANSPORT_VALUE[kind][active ? 1 : 0];
    spokenState.textContent = spoken;
    if (spoken === '') button.removeAttribute('title');
    else button.setAttribute('title', spoken);
    // Play and record are the two controls whose glyph changes shape — play to pause, record
    // to stop. Swapping it is one node replacement, not a re-render of the bar.
    if (kind === 'play' || kind === 'record') {
      glyph.replaceChildren(icon(transportSymbol(kind, active), { size: glyphSize }));
    }
  };

  if (options.active !== undefined) bindReactive(button, options.active, setActive);
  if (options.enabled !== undefined) {
    bindReactive(button, options.enabled, (value) => setDisabled(button, !value));
  }
  pressFeedback(button);

  return { ...baseHandle(button, (enabled) => setDisabled(button, !enabled)), setActive };
}

function transportSymbol(kind: TransportKind, active: boolean): string {
  switch (kind) {
    case 'play':
      return active ? 'pause.fill' : 'play.fill';
    case 'stop':
      return 'stop.fill';
    case 'record':
      // While a take is running this control stops it — the accessible name has always said
      // "Stop recording", and now the glyph agrees. It is the state's second signal, next to
      // the filled red face: a shape change reads in greyscale, where a hue does not.
      return active ? 'stop.fill' : 'record.circle.fill';
    case 'panic':
      return 'exclamationmark';
  }
}

// MARK: - Chip

export interface ChipOptions {
  readonly title: Reactive<string>;
  readonly symbol?: string;
  readonly selected?: Reactive<boolean>;
  readonly enabled?: Reactive<boolean>;
  /**
   * Marks the chip that means "no filter" — All, All folders, Active, None.
   *
   * Selecting it is a rail's *resting* state, not an armed one, so it never takes the acid
   * fill: yellow in this app says something is narrowing, routing or arming, and a library
   * showing every sound is doing none of those. Worse, two default rails stacked (All, and
   * All folders under it) put two large yellow pills on screen for a user who has filtered
   * nothing at all. A resting chip still reads as the live one, through a white label and a
   * heavier weight — text hierarchy rather than a second signal colour, which is also why
   * the distinction survives greyscale.
   */
  readonly resting?: boolean;
  readonly onPress: () => void;
}

export interface ChipHandle extends ControlHandle<HTMLButtonElement> {
  setSelected(selected: boolean): void;
}

/**
 * The pill from the reference board. The pill itself is 32 px because that is what makes a
 * row of them read as tabs rather than as buttons; the element around it is padded out to
 * 44 so the target is honest even though the paint is not.
 */
/*
 * There is deliberately no `role="tab"` option any more. The only caller that used one was
 * the bank rail, and the tab role drags roving tabindex in with it: every unselected chip
 * became `tabindex="-1"`, which is unreachable unless the container implements arrow keys.
 * A chip is a button that knows whether it is selected.
 */
export function chip(options: ChipOptions): ChipHandle {
  const label = h('span', { class: 'pl-chip__label' });
  const pill = h(
    'span',
    { class: 'pl-chip__pill' },
    options.symbol === undefined ? null : icon(options.symbol, { size: 11 }),
    label,
  );
  const button = h(
    'button',
    {
      type: 'button',
      class: options.resting === true ? 'pl-chip pl-chip--resting' : 'pl-chip',
      'aria-pressed': 'false',
      onClick: () => options.onPress(),
    },
    pill,
  );

  bindReactive(label, options.title, (value) => {
    label.textContent = value;
  });

  const setSelected = (selected: boolean): void => {
    button.classList.toggle('is-selected', selected);
    button.setAttribute('aria-pressed', selected ? 'true' : 'false');
  };

  if (options.selected !== undefined) bindReactive(button, options.selected, setSelected);
  if (options.enabled !== undefined) {
    bindReactive(button, options.enabled, (value) => setDisabled(button, !value));
  }
  pressFeedback(button);

  return { ...baseHandle(button, (enabled) => setDisabled(button, !enabled)), setSelected };
}

// MARK: - Chip rail

/**
 * How far each fade on a chip rail reaches in, in CSS pixels. These are the numbers behind
 * `--chip-fade-lead` and `--chip-fade-trail`, and they match `ChipScrollRail` on iOS: a
 * fade reaches full strength once that much content is hidden past its edge.
 */
const CHIP_FADE_LEAD = 12;
const CHIP_FADE_TRAIL = 24;

export interface ChipRowOptions {
  /** Names the rail for assistive technology — "Filters", "Folders". */
  readonly label?: string;
}

/**
 * A horizontally scrolling row of chips — the Library's collections and folders, and every
 * other filter rail.
 *
 * A bare overflow scroller gets one thing badly wrong at phone width: nothing says the row
 * scrolls, so the chip the viewport happens to cut looks like a layout bug rather than like
 * something you can reach. The fix is a short fade over each edge that exists only while
 * there is something hidden past that edge, and is gone the instant there is nothing left
 * to reach — an affordance that cannot lie about what is there.
 *
 * Each strength is written as a 0…1 number into a custom property rather than toggled as a
 * class, so a fade grows with the gesture instead of popping, and an edge with nothing
 * beyond it is exactly 0, which makes that half of the mask a no-op. `scroll-snap` in the
 * stylesheet does the other half of the job: the row can no longer come to rest with a word
 * sliced down the middle.
 */
export function chipRow(options: ChipRowOptions = {}, ...children: Child[]): HTMLElement {
  const row = h(
    'div',
    options.label === undefined
      ? { class: 'chip-row scroll-x' }
      : { class: 'chip-row scroll-x', role: 'group', 'aria-label': options.label },
    ...children,
  );

  const sync = (): void => {
    const behind = row.scrollLeft;
    const ahead = row.scrollWidth - row.clientWidth - row.scrollLeft;
    // Two decimals: a sub-pixel `scrollLeft` would otherwise rewrite the properties on every
    // frame of an inertial scroll for a difference nobody can see. Clamped to 1, so once the
    // row is more than a fade's width from an end the value stops changing entirely.
    row.style.setProperty('--fade-lead', clamp(behind / CHIP_FADE_LEAD, 0, 1).toFixed(2));
    row.style.setProperty('--fade-trail', clamp(ahead / CHIP_FADE_TRAIL, 0, 1).toFixed(2));
  };
  // Through the shared frame queue, so a flick that fires forty scroll events still costs
  // one style write per frame, on the frame the browser was going to paint anyway.
  const syncSoon = (): void => {
    schedule(sync);
  };

  onCleanup(row, on(row, 'scroll', syncSoon, { passive: true }));
  // Rotation, split view, and the keyboard opening all change what fits.
  onCleanup(row, observeSize(row, syncSoon));

  // The folder rail rebuilds its chips whenever a tag is added or removed, which changes
  // how far the row scrolls without changing the row's own box, so a resize observer alone
  // would leave the fade stale — showing "there is more" on a row that no longer has any.
  if (typeof MutationObserver === 'function') {
    const observer = new MutationObserver(syncSoon);
    observer.observe(row, { childList: true });
    onCleanup(row, () => observer.disconnect());
  }

  return row;
}

// MARK: - SegmentedRail

export interface SegmentedItem {
  readonly id: string;
  readonly title: string;
  readonly symbol?: string;
}

export interface SegmentedRailOptions {
  readonly items: readonly SegmentedItem[];
  readonly selection: Reactive<string>;
  /** Names the rail as a whole for assistive technology — "Bank", "Quantise". */
  readonly label: string;
  readonly enabled?: Reactive<boolean>;
  readonly onSelect: (id: string) => void;
}

export interface SegmentedRailHandle extends ControlHandle<HTMLElement> {
  setSelection(id: string): void;
  readonly selection: string;
}

/**
 * A picker for a small fixed set — banks, quantise divisions, playback modes.
 *
 * The indicator is one element moved with `translateX`, not a background that fades in and
 * out on each segment: the eye can follow a thing that travels, and a transform never
 * touches layout, so sliding it cannot cost a frame the audio thread might need.
 */
export function segmentedRail(options: SegmentedRailOptions): SegmentedRailHandle {
  const count = Math.max(1, options.items.length);
  const indicator = h('div', { class: 'pl-rail__indicator', 'aria-hidden': 'true' });
  const buttons: HTMLButtonElement[] = [];
  let current = peekReactive(options.selection);

  const rail = h('div', {
    class: 'pl-rail',
    role: 'radiogroup',
    'aria-label': options.label,
    style: { '--rail-count': String(count) },
  });
  rail.appendChild(indicator);

  const applySelection = (id: string): void => {
    current = id;
    let index = options.items.findIndex((item) => item.id === id);
    if (index < 0) index = 0;
    indicator.style.setProperty('--rail-index', String(index));
    indicator.classList.toggle('is-hidden', options.items.length === 0);
    for (let i = 0; i < buttons.length; i++) {
      const button = buttons[i];
      if (button === undefined) continue;
      const selected = i === index;
      button.classList.toggle('is-selected', selected);
      button.setAttribute('aria-checked', selected ? 'true' : 'false');
      button.setAttribute('tabindex', selected ? '0' : '-1');
    }
  };

  const move = (delta: number): void => {
    const index = options.items.findIndex((item) => item.id === current);
    const next = options.items[(Math.max(0, index) + delta + count) % count];
    if (next === undefined) return;
    options.onSelect(next.id);
    applySelection(next.id);
    const target = buttons[options.items.indexOf(next)];
    target?.focus();
  };

  for (const item of options.items) {
    const button = h(
      'button',
      {
        type: 'button',
        class: 'pl-rail__segment',
        role: 'radio',
        'aria-checked': 'false',
        tabindex: '-1',
        onClick: () => {
          options.onSelect(item.id);
          applySelection(item.id);
        },
        onKeyDown: (event: Event) => {
          const key = (event as KeyboardEvent).key;
          if (key === 'ArrowRight' || key === 'ArrowDown') {
            event.preventDefault();
            move(1);
          } else if (key === 'ArrowLeft' || key === 'ArrowUp') {
            event.preventDefault();
            move(-1);
          }
        },
      },
      item.symbol === undefined ? null : icon(item.symbol, { size: 13 }),
      h('span', { class: 'pl-rail__title' }, item.title),
    );
    pressFeedback(button);
    buttons.push(button);
    rail.appendChild(button);
  }

  bindReactive(rail, options.selection, applySelection);
  if (options.enabled !== undefined) {
    bindReactive(rail, options.enabled, (value) => {
      rail.classList.toggle('is-disabled', !value);
      for (const button of buttons) button.disabled = !value;
    });
  }

  return {
    ...baseHandle(rail, (enabled) => {
      rail.classList.toggle('is-disabled', !enabled);
      for (const button of buttons) button.disabled = !enabled;
    }),
    setSelection: applySelection,
    get selection() {
      return current;
    },
  };
}

// MARK: - Toggle

export interface ToggleOptions {
  readonly title: Reactive<string>;
  readonly subtitle?: Reactive<string>;
  readonly on: Reactive<boolean>;
  readonly enabled?: Reactive<boolean>;
  readonly onChange: (on: boolean) => void;
}

export interface ToggleHandle extends ControlHandle<HTMLButtonElement> {
  setOn(on: boolean): void;
  readonly isOn: boolean;
}

export function toggle(options: ToggleOptions): ToggleHandle {
  const title = h('span', { class: 'pl-toggle__title' });
  const subtitle =
    options.subtitle === undefined ? null : h('span', { class: 'pl-toggle__subtitle' });
  const track = h('span', { class: 'pl-toggle__track', 'aria-hidden': 'true' }, h('span', { class: 'pl-toggle__knob' }));

  let current = peekReactive(options.on);

  const button = h(
    'button',
    {
      type: 'button',
      class: 'pl-toggle',
      role: 'switch',
      'aria-checked': current ? 'true' : 'false',
      onClick: () => {
        const next = !current;
        setOn(next);
        options.onChange(next);
      },
    },
    h('span', { class: 'pl-toggle__text' }, title, subtitle),
    track,
  );

  function setOn(on: boolean): void {
    current = on;
    button.classList.toggle('is-on', on);
    button.setAttribute('aria-checked', on ? 'true' : 'false');
  }

  bindReactive(title, options.title, (value) => {
    title.textContent = value;
  });
  if (subtitle !== null && options.subtitle !== undefined) {
    bindReactive(subtitle, options.subtitle, (value) => {
      subtitle.textContent = value;
    });
  }
  bindReactive(button, options.on, setOn);
  if (options.enabled !== undefined) {
    bindReactive(button, options.enabled, (value) => setDisabled(button, !value));
  }
  pressFeedback(button);

  return {
    ...baseHandle(button, (enabled) => setDisabled(button, !enabled)),
    setOn,
    get isOn() {
      return current;
    },
  };
}

// MARK: - ValueStepper

export interface ValueStepperOptions {
  readonly label: string;
  readonly value: Reactive<number>;
  readonly min: number;
  readonly max: number;
  readonly step?: number;
  readonly unit?: string;
  readonly format?: (value: number) => string;
  readonly enabled?: Reactive<boolean>;
  readonly onChange: (value: number) => void;
}

export interface ValueStepperHandle extends ControlHandle<HTMLElement> {
  setValue(value: number): void;
  readonly value: number;
}

export function valueStepper(options: ValueStepperOptions): ValueStepperHandle {
  const step = Math.max(options.step ?? 1, Number.EPSILON);
  const format = options.format ?? ((value: number) => String(Math.round(value)));
  let current = clamp(peekReactive(options.value), options.min, options.max);

  const readout = h('span', { class: 'pl-stepper__value numeric' });
  const unit =
    options.unit === undefined ? null : h('span', { class: 'pl-stepper__unit' }, options.unit);
  /**
   * The control's own name, drawn on the control.
   *
   * `options.label` used to reach `aria-label` and nowhere else, so every stepper in the app
   * was an anonymous box of digits: the Sequencer's Timing row showed "92 BPM" and "0 %" with
   * the words Tempo and Swing nowhere on screen, and the wave editor's cents control rendered
   * as a bare "0 ¢" beside a "1.00×", which is why it read as a tempo trim. Inside the
   * spinbutton, so `aria-label` still wins for VoiceOver and nothing is said twice.
   */
  const name = h('span', { class: 'kicker pl-stepper__name' }, options.label);

  const spoken = (): string =>
    options.unit === undefined ? format(current) : `${format(current)} ${options.unit}`;

  const render = (): void => {
    readout.textContent = format(current);
    row.setAttribute('aria-valuenow', String(current));
    row.setAttribute('aria-valuetext', spoken());
    minus.disabled = current <= options.min + step * 0.001;
    plus.disabled = current >= options.max - step * 0.001;
  };

  /**
   * Snaps to the step grid measured from the lower bound, so repeated taps land on round
   * numbers instead of drifting by whatever remainder the starting value carried.
   */
  const adjust = (delta: number): void => {
    const raw = current + delta;
    const snapped = options.min + Math.round((raw - options.min) / step) * step;
    const next = clamp(snapped, options.min, options.max);
    if (next === current) return;
    current = next;
    render();
    options.onChange(next);
  };

  const minus = repeatButton('minus', () => adjust(-step));
  const plus = repeatButton('plus', () => adjust(step));

  const row = h(
    'div',
    {
      class: 'pl-stepper',
      role: 'spinbutton',
      tabindex: '0',
      'aria-label': options.label,
      'aria-valuemin': String(options.min),
      'aria-valuemax': String(options.max),
      onKeyDown: (event: Event) => {
        const key = (event as KeyboardEvent).key;
        if (key === 'ArrowUp' || key === 'ArrowRight') {
          event.preventDefault();
          adjust(step);
        } else if (key === 'ArrowDown' || key === 'ArrowLeft') {
          event.preventDefault();
          adjust(-step);
        } else if (key === 'PageUp') {
          event.preventDefault();
          adjust(step * 10);
        } else if (key === 'PageDown') {
          event.preventDefault();
          adjust(-step * 10);
        } else if (key === 'Home') {
          event.preventDefault();
          adjust(options.min - current);
        } else if (key === 'End') {
          event.preventDefault();
          adjust(options.max - current);
        }
      },
    },
    minus,
    hairlineDivider('vertical'),
    // The value and its unit share a baseline, and that pair is centred as one block against
    // the +/- buttons. Baseline-aligning them directly inside the stretched readout parked the
    // number at the top of the line box, which is why every stepper in the app sat high.
    h('span', { class: 'pl-stepper__readout' }, name, h('span', { class: 'pl-stepper__line' }, readout, unit)),
    hairlineDivider('vertical'),
    plus,
  );

  bindReactive(row, options.value, (value) => {
    current = clamp(value, options.min, options.max);
    render();
  });
  if (options.enabled !== undefined) {
    bindReactive(row, options.enabled, (value) => setDisabled(row, !value));
  }
  render();

  return {
    ...baseHandle(row, (enabled) => setDisabled(row, !enabled)),
    setValue(value: number): void {
      current = clamp(value, options.min, options.max);
      render();
    },
    get value() {
      return current;
    },
  };
}

/**
 * Press-and-hold key repeat with acceleration, private to the stepper because the curve is
 * tuned for a numeric readout: a deliberate pause first so a normal tap can never be read
 * as a hold, then a run that lets a tempo travel 100 BPM without lifting a finger.
 *
 * Hidden from assistive technology — the stepper exposes one adjustable element, and two
 * extra buttons would make every value change a three-swipe operation in VoiceOver.
 */
function repeatButton(symbol: string, action: () => void): HTMLButtonElement {
  let timer = 0;
  let interval = 130;

  const stop = (): void => {
    if (timer !== 0) {
      clearTimeout(timer);
      timer = 0;
    }
    interval = 130;
    button.classList.remove('is-pressed');
  };

  const run = (): void => {
    action();
    interval = Math.max(40, interval - 14);
    timer = self.setTimeout(run, interval);
  };

  const button = h('button', {
    type: 'button',
    class: 'pl-stepper__button',
    tabindex: '-1',
    'aria-hidden': 'true',
    onPointerDown: [
      (event: Event) => {
        const pointer = event as PointerEvent;
        if (button.disabled) return;
        event.preventDefault();
        button.classList.add('is-pressed');
        capturePointer(button, pointer.pointerId);
        action();
        timer = self.setTimeout(run, 420);
      },
      { passive: false },
    ],
    onPointerUp: stop,
    onPointerCancel: stop,
    onLostPointerCapture: stop,
  }, icon(symbol, { size: 15 }));

  onCleanup(button, stop);
  return button;
}

// MARK: - Descriptor stepping
//
// One VoiceOver / arrow-key step is a twentieth of the range, which is enough resolution to
// set anything precisely without making a sweep tedious. A logarithmic parameter steps in
// normalised space: a fixed number of hertz would crawl at the bottom and leap at the top.

function steppedValue(descriptor: EffectParameter, from: number, direction: 1 | -1): number {
  switch (descriptor.scale) {
    case 'toggle':
      return direction > 0 ? 1 : 0;
    case 'stepped': {
      const raw = (descriptor.max - descriptor.min) / 20;
      return effectParameterClamp(descriptor, from + direction * Math.max(1, Math.round(raw)));
    }
    case 'linear':
      return effectParameterClamp(descriptor, from + (direction * (descriptor.max - descriptor.min)) / 20);
    case 'logarithmic': {
      const position = effectParameterNormalize(descriptor, from) + direction * 0.05;
      return effectParameterDenormalize(descriptor, clamp(position, 0, 1));
    }
  }
}

/** True when the parameter reads as "cut or boost around zero" and should fill from centre. */
function isBipolar(descriptor: EffectParameter): boolean {
  return descriptor.scale !== 'toggle' && descriptor.min < 0 && descriptor.max > 0;
}

export interface ParameterControlOptions {
  readonly descriptor: EffectParameter;
  readonly value: Reactive<number>;
  readonly tint?: string;
  readonly enabled?: Reactive<boolean>;
  /** Fires on every intermediate value — automation recording needs the whole gesture. */
  readonly onChange: (value: number) => void;
  /** Fires once, on release, for callers that only want to commit an edit. */
  readonly onCommit?: (value: number) => void;
}

export interface ParameterControlHandle extends ControlHandle<HTMLElement> {
  setValue(value: number): void;
  readonly value: number;
}

/** Shared keyboard map for anything driven by an `EffectParameter`. */
function parameterKeydown(
  descriptor: EffectParameter,
  read: () => number,
  write: (value: number) => void,
): (event: Event) => void {
  return (event: Event) => {
    const key = (event as KeyboardEvent).key;
    const current = read();
    switch (key) {
      case 'ArrowUp':
      case 'ArrowRight':
        event.preventDefault();
        write(steppedValue(descriptor, current, 1));
        return;
      case 'ArrowDown':
      case 'ArrowLeft':
        event.preventDefault();
        write(steppedValue(descriptor, current, -1));
        return;
      case 'PageUp':
        event.preventDefault();
        write(effectParameterDenormalize(descriptor, clamp(effectParameterNormalize(descriptor, current) + 0.2, 0, 1)));
        return;
      case 'PageDown':
        event.preventDefault();
        write(effectParameterDenormalize(descriptor, clamp(effectParameterNormalize(descriptor, current) - 0.2, 0, 1)));
        return;
      case 'Home':
        event.preventDefault();
        write(descriptor.min);
        return;
      case 'End':
        event.preventDefault();
        write(descriptor.max);
        return;
      case 'Backspace':
      case 'Delete':
        event.preventDefault();
        write(descriptor.defaultValue);
        return;
      default:
    }
  };
}

// MARK: - Knob

export interface KnobOptions extends ParameterControlOptions {
  readonly diameter?: number;
  readonly showsName?: boolean;
  readonly showsValue?: boolean;
}

const KNOB_ARC_START = 135;
const KNOB_ARC_SWEEP = 270;
/** CSS pixels of vertical travel that sweep the whole range. */
const KNOB_COARSE_TRAVEL = 170;
const KNOB_FINE_TRAVEL = 900;
const KNOB_FINE_HOLD_MS = 350;
const KNOB_DOUBLE_TAP_MS = 400;

/**
 * A vertical-drag parameter knob.
 *
 * Deliberately not a rotary gesture: circular tracking is imprecise on a touch screen and
 * impossible one-handed. The dial *looks* like a knob and *behaves* like a fader — which
 * is also what every hardware controller does with an encoder under a finger.
 */
export function knob(options: KnobOptions): ParameterControlHandle {
  const { descriptor } = options;
  const diameter = options.diameter ?? 60;
  let current = effectParameterClamp(descriptor, peekReactive(options.value));

  const name = options.showsName === false ? null : kickerLabel(descriptor.name, { class: 'pl-knob__name' });
  const readout = options.showsValue === false ? null : h('span', { class: 'pl-knob__value numeric' });
  const fineBadge = h('span', { class: 'pl-knob__fine kicker' }, 'Fine');

  const surface = createCanvasSurface('pl-knob__dial', () => draw());
  surface.canvas.style.setProperty('width', `${diameter}px`);
  surface.canvas.style.setProperty('height', `${diameter}px`);

  const hintId = `pl-knob-hint-${nextId()}`;
  const hint = h(
    'span',
    { class: 'visually-hidden', id: hintId },
    `Drag up or down to change. Press Backspace to reset to ${effectParameterFormat(descriptor, descriptor.defaultValue)}.`,
  );

  const root = h(
    'div',
    {
      class: 'pl-knob',
      role: 'slider',
      tabindex: '0',
      'aria-label': descriptor.name,
      'aria-valuemin': String(descriptor.min),
      'aria-valuemax': String(descriptor.max),
      'aria-describedby': hintId,
      style: {
        '--knob-size': `${diameter}px`,
        '--knob-tint': options.tint ?? 'var(--accent-magenta)',
      },
      onKeyDown: parameterKeydown(
        descriptor,
        () => current,
        (next) => {
          apply(next, true);
          options.onCommit?.(current);
        },
      ),
    },
    name,
    h('div', { class: 'pl-knob__dialwrap' }, surface.canvas, fineBadge),
    readout,
    hint,
  );

  function render(): void {
    root.setAttribute('aria-valuenow', String(current));
    root.setAttribute('aria-valuetext', effectParameterFormat(descriptor, current));
    if (readout !== null) readout.textContent = effectParameterFormat(descriptor, current);
    draw();
  }

  function apply(raw: number, notify: boolean): void {
    const next = effectParameterClamp(descriptor, raw);
    if (next === current) return;
    current = next;
    render();
    if (notify) options.onChange(next);
  }

  function draw(): void {
    const context = surface.context;
    if (typeof context.clearRect !== 'function') return;
    const width = surface.width;
    const height = surface.height;
    if (width < 4 || height < 4) return;
    context.clearRect(0, 0, width, height);

    const tint = resolveColour(options.tint, '#ec5cff');
    const trackColour = themeToken('--hairline-strong', '#33333e');
    const faceColour = themeToken('--ink-raised', '#17171c');
    const rimColour = themeToken('--hairline', '#24242c');
    const baseColour = themeToken('--ink-base', '#08080a');
    const pointerColour = root.classList.contains('is-editing')
      ? themeToken('--tone-primary', '#ffffff')
      : themeToken('--tone-secondary', '#9195a3');

    const lineWidth = Math.max(3, Math.min(width, height) * 0.09);
    const radius = (Math.min(width, height) - lineWidth) / 2 - 1;
    if (radius <= 2) return;
    const cx = width / 2;
    const cy = height / 2;
    const position = effectParameterNormalize(descriptor, current);
    const toRad = (degrees: number): number => (degrees * Math.PI) / 180;

    const faceRadius = Math.max(1, radius - lineWidth * 0.9);
    context.beginPath();
    context.arc(cx, cy, faceRadius, 0, Math.PI * 2);
    context.fillStyle = faceColour;
    context.fill();
    context.lineWidth = 1;
    context.strokeStyle = rimColour;
    context.stroke();

    context.lineCap = 'round';
    context.lineWidth = lineWidth;
    context.strokeStyle = trackColour;
    context.beginPath();
    context.arc(cx, cy, radius, toRad(KNOB_ARC_START), toRad(KNOB_ARC_START + KNOB_ARC_SWEEP));
    context.stroke();

    const origin = isBipolar(descriptor) ? effectParameterNormalize(descriptor, 0) : 0;
    const lower = KNOB_ARC_START + KNOB_ARC_SWEEP * Math.min(origin, position);
    const upper = KNOB_ARC_START + KNOB_ARC_SWEEP * Math.max(origin, position);
    if (upper - lower > 0.4) {
      context.strokeStyle = tint;
      context.beginPath();
      context.arc(cx, cy, radius, toRad(lower), toRad(upper));
      context.stroke();
    }

    // Where a double tap lands. A visible home position is the difference between a reset
    // the user trusts and one they have to discover.
    const homeAngle = toRad(
      KNOB_ARC_START + KNOB_ARC_SWEEP * effectParameterNormalize(descriptor, descriptor.defaultValue),
    );
    context.beginPath();
    context.arc(cx + Math.cos(homeAngle) * radius, cy + Math.sin(homeAngle) * radius, 1.35, 0, Math.PI * 2);
    context.fillStyle = baseColour;
    context.fill();

    const angle = toRad(KNOB_ARC_START + KNOB_ARC_SWEEP * position);
    const outer = faceRadius - 3;
    const inner = outer * 0.4;
    context.beginPath();
    context.moveTo(cx + Math.cos(angle) * inner, cy + Math.sin(angle) * inner);
    context.lineTo(cx + Math.cos(angle) * outer, cy + Math.sin(angle) * outer);
    context.lineWidth = 2.5;
    context.strokeStyle = pointerColour;
    context.stroke();
  }

  // --- Gesture ---------------------------------------------------------------------

  let pointerId: number | null = null;
  let anchorPosition = 0;
  let anchorY = 0;
  let lastY = 0;
  let maxDisplacement = 0;
  let fine = false;
  let fineTimer = 0;
  let lastTapAt = 0;

  const engageFine = (): void => {
    // Re-anchor before switching travel scales, or the value jumps by whatever the finger
    // had already moved before the hold was recognised.
    fine = true;
    anchorPosition = effectParameterNormalize(descriptor, current);
    anchorY = lastY;
    root.classList.add('is-fine');
  };

  const endGesture = (): void => {
    if (fineTimer !== 0) {
      clearTimeout(fineTimer);
      fineTimer = 0;
    }
    pointerId = null;
    fine = false;
    root.classList.remove('is-editing', 'is-fine');
  };

  onCleanup(
    root,
    on(root, 'pointerdown', (event: Event) => {
      const pointer = event as PointerEvent;
      if (pointerId !== null || root.getAttribute('aria-disabled') === 'true') return;
      event.preventDefault();
      pointerId = pointer.pointerId;
      capturePointer(root, pointer.pointerId);
      root.classList.add('is-editing');
      anchorPosition = effectParameterNormalize(descriptor, current);
      anchorY = pointer.clientY;
      lastY = pointer.clientY;
      maxDisplacement = 0;
      fine = false;
      fineTimer = self.setTimeout(() => {
        fineTimer = 0;
        if (pointerId !== null && !fine && maxDisplacement < 8) engageFine();
      }, KNOB_FINE_HOLD_MS);
      render();
    }),
  );

  onCleanup(
    root,
    on(root, 'pointermove', (event: Event) => {
      const pointer = event as PointerEvent;
      if (pointer.pointerId !== pointerId) return;
      lastY = pointer.clientY;
      maxDisplacement = Math.max(maxDisplacement, Math.abs(pointer.clientY - anchorY));
      const travel = fine ? KNOB_FINE_TRAVEL : KNOB_COARSE_TRAVEL;
      const delta = -(pointer.clientY - anchorY) / travel;
      let candidate = effectParameterDenormalize(descriptor, clamp(anchorPosition + delta, 0, 1));

      // Continuous parameters snap to their default so "back to flat" is reachable without
      // staring at the readout. Fine mode turns the snap off — landing just off centre is
      // the entire point of fine mode.
      if (!fine && (descriptor.scale === 'linear' || descriptor.scale === 'logarithmic')) {
        const home = effectParameterNormalize(descriptor, descriptor.defaultValue);
        if (Math.abs(clamp(anchorPosition + delta, 0, 1) - home) < 0.015) {
          candidate = descriptor.defaultValue;
        }
      }
      apply(candidate, true);
    }),
  );

  const finish = (event: Event): void => {
    const pointer = event as PointerEvent;
    if (pointer.pointerId !== pointerId) return;
    const wasTap = maxDisplacement < 6;
    const now = pointer.timeStamp;
    if (wasTap) {
      if (now - lastTapAt < KNOB_DOUBLE_TAP_MS) {
        apply(descriptor.defaultValue, true);
        lastTapAt = 0;
      } else {
        lastTapAt = now;
      }
    }
    endGesture();
    render();
    options.onCommit?.(current);
  };

  onCleanup(root, on(root, 'pointerup', finish));
  onCleanup(root, on(root, 'pointercancel', (event: Event) => {
    const pointer = event as PointerEvent;
    if (pointer.pointerId !== pointerId) return;
    endGesture();
    render();
  }));

  bindReactive(root, options.value, (value) => {
    if (pointerId !== null) return; // A live gesture owns the value; do not fight it.
    current = effectParameterClamp(descriptor, value);
    render();
  });
  if (options.enabled !== undefined) {
    bindReactive(root, options.enabled, (value) => setDisabled(root, !value));
  }

  onCleanup(root, () => {
    endGesture();
    surface.dispose();
  });

  surface.measure();
  render();

  return {
    ...baseHandle(root, (enabled) => setDisabled(root, !enabled)),
    setValue(value: number): void {
      current = effectParameterClamp(descriptor, value);
      render();
    },
    get value() {
      return current;
    },
  };
}

// MARK: - ParameterRow

export interface ParameterRowOptions extends ParameterControlOptions {
  /** Overrides the descriptor's own name for the visible label. */
  readonly title?: string;
}

/**
 * The horizontal form of the knob, for parameter lists where a row of dials would waste
 * the width. Same descriptor contract, same keyboard map, same spoken value.
 */
export function parameterRow(options: ParameterRowOptions): ParameterControlHandle {
  const { descriptor } = options;
  let current = effectParameterClamp(descriptor, peekReactive(options.value));

  const readout = h('span', { class: 'pl-param__value numeric' });
  const surface = createCanvasSurface('pl-param__track', () => draw());

  const track = h('div', {
    class: 'pl-param__trackwrap',
    role: 'slider',
    tabindex: '0',
    'aria-label': options.title ?? descriptor.name,
    'aria-valuemin': String(descriptor.min),
    'aria-valuemax': String(descriptor.max),
    onKeyDown: parameterKeydown(
      descriptor,
      () => current,
      (next) => {
        apply(next, true);
        options.onCommit?.(current);
      },
    ),
  }, surface.canvas);

  const root = h(
    'div',
    {
      class: 'pl-param',
      style: { '--param-tint': options.tint ?? 'var(--accent-magenta)' },
    },
    kickerLabel(options.title ?? descriptor.name, { class: 'pl-param__name' }),
    track,
    readout,
  );

  function render(): void {
    const formatted = effectParameterFormat(descriptor, current);
    readout.textContent = formatted;
    track.setAttribute('aria-valuenow', String(current));
    track.setAttribute('aria-valuetext', formatted);
    draw();
  }

  function apply(raw: number, notify: boolean): void {
    const next = effectParameterClamp(descriptor, raw);
    if (next === current) return;
    current = next;
    render();
    if (notify) options.onChange(next);
  }

  const THUMB = 16;

  function draw(): void {
    const context = surface.context;
    if (typeof context.clearRect !== 'function') return;
    const width = surface.width;
    const height = surface.height;
    if (width < 8 || height < 4) return;
    context.clearRect(0, 0, width, height);

    const tint = resolveColour(options.tint, '#ec5cff');
    const trackColour = themeToken('--hairline-strong', '#33333e');
    const tickColour = themeToken('--hairline', '#24242c');
    const thumbColour = themeToken('--tone-primary', '#ffffff');

    const midY = height / 2;
    const left = THUMB / 2;
    const right = width - THUMB / 2;
    if (right <= left) return;
    const trackHeight = 6;

    context.fillStyle = trackColour;
    roundedRect(context, left, midY - trackHeight / 2, right - left, trackHeight, trackHeight / 2);
    context.fill();

    // Discrete parameters show where their steps are; on a continuous one the same ticks
    // would just look like a dirty track.
    if (descriptor.scale === 'stepped') {
      const steps = Math.round(descriptor.max - descriptor.min);
      if (steps > 0 && steps <= 24) {
        context.strokeStyle = tickColour;
        context.lineWidth = 1;
        context.beginPath();
        for (let index = 0; index <= steps; index++) {
          const x = left + ((right - left) * index) / steps;
          context.moveTo(x, midY - trackHeight);
          context.lineTo(x, midY + trackHeight);
        }
        context.stroke();
      }
    }

    const position = effectParameterNormalize(descriptor, current);
    const origin = isBipolar(descriptor) ? effectParameterNormalize(descriptor, 0) : 0;
    const x0 = left + (right - left) * Math.min(position, origin);
    const x1 = left + (right - left) * Math.max(position, origin);
    if (x1 - x0 > 0.5) {
      context.fillStyle = tint;
      roundedRect(context, x0, midY - trackHeight / 2, x1 - x0, trackHeight, trackHeight / 2);
      context.fill();
    }

    const thumbX = left + (right - left) * position;
    context.beginPath();
    context.arc(thumbX, midY, THUMB / 2, 0, Math.PI * 2);
    context.fillStyle = thumbColour;
    context.fill();
    context.lineWidth = 2;
    context.strokeStyle = tint;
    context.stroke();
  }

  // --- Gesture ---------------------------------------------------------------------

  let pointerId: number | null = null;

  const positionFor = (clientX: number): number => {
    const rect = surface.canvas.getBoundingClientRect();
    const usable = Math.max(1, rect.width - THUMB);
    return clamp((clientX - rect.left - THUMB / 2) / usable, 0, 1);
  };

  onCleanup(
    track,
    on(track, 'pointerdown', (event: Event) => {
      const pointer = event as PointerEvent;
      if (pointerId !== null || track.getAttribute('aria-disabled') === 'true') return;
      event.preventDefault();
      pointerId = pointer.pointerId;
      capturePointer(track, pointer.pointerId);
      track.classList.add('is-editing');
      apply(effectParameterDenormalize(descriptor, positionFor(pointer.clientX)), true);
    }),
  );

  onCleanup(
    track,
    on(track, 'pointermove', (event: Event) => {
      const pointer = event as PointerEvent;
      if (pointer.pointerId !== pointerId) return;
      apply(effectParameterDenormalize(descriptor, positionFor(pointer.clientX)), true);
    }),
  );

  const release = (event: Event): void => {
    const pointer = event as PointerEvent;
    if (pointer.pointerId !== pointerId) return;
    pointerId = null;
    track.classList.remove('is-editing');
    options.onCommit?.(current);
  };
  onCleanup(track, on(track, 'pointerup', release));
  onCleanup(track, on(track, 'pointercancel', release));

  bindReactive(root, options.value, (value) => {
    if (pointerId !== null) return;
    current = effectParameterClamp(descriptor, value);
    render();
  });
  if (options.enabled !== undefined) {
    bindReactive(root, options.enabled, (value) => {
      root.classList.toggle('is-disabled', !value);
      setDisabled(track, !value);
    });
  }
  onCleanup(root, () => surface.dispose());

  surface.measure();
  render();

  return {
    ...baseHandle(root, (enabled) => {
      root.classList.toggle('is-disabled', !enabled);
      setDisabled(track, !enabled);
    }),
    setValue(value: number): void {
      current = effectParameterClamp(descriptor, value);
      render();
    },
    get value() {
      return current;
    },
  };
}

// MARK: - XYPad

export interface XYPadOptions {
  readonly xLabel: string;
  readonly yLabel: string;
  readonly x?: Reactive<number>;
  readonly y?: Reactive<number>;
  readonly tint?: string;
  readonly showsGrid?: boolean;
  /**
   * Latched: the puck stays where it was left. Momentary: it springs back to centre on
   * release, which is what a filter-sweep performance control wants.
   */
  readonly latched?: boolean;
  readonly enabled?: Reactive<boolean>;
  /** Fires on every intermediate position — the Sound Lab records these into automation. */
  readonly onChange: (x: number, y: number) => void;
  readonly onEnd?: (x: number, y: number) => void;
}

export interface XYPadHandle extends ControlHandle<HTMLElement> {
  setPosition(x: number, y: number): void;
  /**
   * Renames both axes.
   *
   * The labels used to be baked into the DOM at construction, so the Sound Lab's two
   * assignment rails changed what the axes *did* while the pad, the group name and both
   * hidden sliders went on saying "Filter" and "Pitch" for the life of the screen.
   */
  setLabels(xLabel: string, yLabel: string): void;
  readonly position: { readonly x: number; readonly y: number };
}

const XY_INSET = 18;
const XY_TRAIL_LIMIT = 16;

export function xyPad(options: XYPadOptions): XYPadHandle {
  const latched = options.latched ?? true;
  let x = clamp(peekReactive(options.x ?? 0.5), 0, 1);
  let y = clamp(peekReactive(options.y ?? 0.5), 0, 1);
  const trail: Array<{ x: number; y: number }> = [];

  const surface = createCanvasSurface('pl-xy__field', () => draw());
  const puck = h('div', { class: 'pl-xy__puck', 'aria-hidden': 'true' });
  const readout = h('span', { class: 'pl-xy__readout numeric' });

  const percent = (value: number): number => Math.round(clamp(value, 0, 1) * 100);

  const axisSlider = (
    label: string,
    axis: 'x' | 'y',
  ): HTMLElement =>
    h('div', {
      // Not `visually-hidden`: that clips to a 1 px box, and these are focusable, so a
      // keyboard user watched the focus ring vanish into nothing twice per pad. They are
      // laid out along the edges they control and painted only while focused.
      class: `pl-xy__axis pl-xy__axis--${axis}`,
      role: 'slider',
      tabindex: '0',
      'aria-label': label,
      'aria-valuemin': '0',
      'aria-valuemax': '100',
      'aria-valuenow': String(percent(axis === 'x' ? x : y)),
      'aria-valuetext': `${percent(axis === 'x' ? x : y)} percent`,
      onKeyDown: (event: Event) => {
        const key = (event as KeyboardEvent).key;
        const step = key === 'PageUp' || key === 'PageDown' ? 0.1 : 0.02;
        let delta = 0;
        if (key === 'ArrowUp' || key === 'ArrowRight' || key === 'PageUp') delta = step;
        else if (key === 'ArrowDown' || key === 'ArrowLeft' || key === 'PageDown') delta = -step;
        else if (key === 'Home') delta = -1;
        else if (key === 'End') delta = 1;
        else return;
        event.preventDefault();
        if (axis === 'x') setPosition(clamp(x + delta, 0, 1), y);
        else setPosition(x, clamp(y + delta, 0, 1));
        options.onChange(x, y);
        options.onEnd?.(x, y);
      },
    });

  let xLabel = options.xLabel;
  let yLabel = options.yLabel;
  const xAxis = axisSlider(xLabel, 'x');
  const yAxis = axisSlider(yLabel, 'y');
  const xLabelText = h('span', { class: 'kicker pl-xy__xlabel' }, xLabel);
  const yLabelText = h('span', { class: 'kicker pl-xy__ylabel' }, yLabel);

  const root = h(
    'div',
    {
      class: 'pl-xy',
      role: 'group',
      'aria-label': `${xLabel} and ${yLabel} pad`,
      style: { '--xy-tint': options.tint ?? 'var(--accent-magenta)' },
    },
    surface.canvas,
    puck,
    h('div', { class: 'pl-xy__labels', 'aria-hidden': 'true' }, yLabelText, readout, xLabelText),
    xAxis,
    yAxis,
  );

  function setLabels(nextX: string, nextY: string): void {
    if (nextX === xLabel && nextY === yLabel) return;
    xLabel = nextX;
    yLabel = nextY;
    xLabelText.textContent = xLabel;
    yLabelText.textContent = yLabel;
    xAxis.setAttribute('aria-label', xLabel);
    yAxis.setAttribute('aria-label', yLabel);
    root.setAttribute('aria-label', `${xLabel} and ${yLabel} pad`);
  }

  function render(): void {
    readout.textContent = `${percent(x)}% · ${percent(y)}%`;
    xAxis.setAttribute('aria-valuenow', String(percent(x)));
    xAxis.setAttribute('aria-valuetext', `${percent(x)} percent`);
    yAxis.setAttribute('aria-valuenow', String(percent(y)));
    yAxis.setAttribute('aria-valuetext', `${percent(y)} percent`);
    const width = surface.width;
    const height = surface.height;
    const usableW = Math.max(1, width - XY_INSET * 2);
    const usableH = Math.max(1, height - XY_INSET * 2);
    // translate3d rather than left/top: the puck must never trigger layout while a finger
    // is drawing an automation curve through it.
    puck.style.setProperty(
      'transform',
      `translate3d(${XY_INSET + usableW * x}px, ${XY_INSET + usableH * (1 - y)}px, 0) translate(-50%, -50%)`,
    );
    draw();
  }

  function setPosition(nextX: number, nextY: number): void {
    x = clamp(nextX, 0, 1);
    y = clamp(nextY, 0, 1);
    render();
  }

  function draw(): void {
    const context = surface.context;
    if (typeof context.clearRect !== 'function') return;
    const width = surface.width;
    const height = surface.height;
    if (width < 8 || height < 8) return;
    context.clearRect(0, 0, width, height);

    const tint = resolveColour(options.tint, '#ec5cff');
    const gridColour = themeToken('--hairline', '#24242c');
    const centreColour = themeToken('--hairline-strong', '#33333e');

    if (options.showsGrid !== false) {
      context.lineWidth = 1;
      context.strokeStyle = gridColour;
      context.beginPath();
      for (let index = 1; index < 4; index++) {
        const fraction = index / 4;
        const gx = XY_INSET + (width - XY_INSET * 2) * fraction;
        const gy = XY_INSET + (height - XY_INSET * 2) * fraction;
        context.moveTo(gx, XY_INSET);
        context.lineTo(gx, height - XY_INSET);
        context.moveTo(XY_INSET, gy);
        context.lineTo(width - XY_INSET, gy);
      }
      context.stroke();

      context.strokeStyle = centreColour;
      context.beginPath();
      context.moveTo(width / 2, XY_INSET);
      context.lineTo(width / 2, height - XY_INSET);
      context.moveTo(XY_INSET, height / 2);
      context.lineTo(width - XY_INSET, height / 2);
      context.stroke();
    }

    // Oldest segment faintest: the trail reads as direction of travel, which is what makes
    // a fast gesture legible at all.
    if (trail.length > 1 && !prefersReducedMotion()) {
      const usableW = Math.max(1, width - XY_INSET * 2);
      const usableH = Math.max(1, height - XY_INSET * 2);
      context.lineCap = 'round';
      context.strokeStyle = tint;
      for (let index = 1; index < trail.length; index++) {
        const from = trail[index - 1];
        const to = trail[index];
        if (from === undefined || to === undefined) continue;
        const age = index / trail.length;
        context.globalAlpha = 0.06 + 0.34 * age;
        context.lineWidth = 1 + 3 * age;
        context.beginPath();
        context.moveTo(XY_INSET + usableW * from.x, XY_INSET + usableH * (1 - from.y));
        context.lineTo(XY_INSET + usableW * to.x, XY_INSET + usableH * (1 - to.y));
        context.stroke();
      }
      context.globalAlpha = 1;
    }
  }

  // --- Gesture ---------------------------------------------------------------------

  let pointerId: number | null = null;

  const valuesAt = (clientX: number, clientY: number): { x: number; y: number } => {
    const rect = surface.canvas.getBoundingClientRect();
    const usableW = Math.max(1, rect.width - XY_INSET * 2);
    const usableH = Math.max(1, rect.height - XY_INSET * 2);
    return {
      x: clamp((clientX - rect.left - XY_INSET) / usableW, 0, 1),
      // Screen y grows downward; the value axis grows upward, the way a performer expects
      // "more" to be higher.
      y: clamp(1 - (clientY - rect.top - XY_INSET) / usableH, 0, 1),
    };
  };

  onCleanup(
    root,
    on(root, 'pointerdown', (event: Event) => {
      const pointer = event as PointerEvent;
      if (pointerId !== null || root.classList.contains('is-disabled')) return;
      if (pointer.target === xAxis || pointer.target === yAxis) return;
      event.preventDefault();
      pointerId = pointer.pointerId;
      capturePointer(root, pointer.pointerId);
      root.classList.add('is-dragging');
      trail.length = 0;
      const next = valuesAt(pointer.clientX, pointer.clientY);
      setPosition(next.x, next.y);
      options.onChange(x, y);
    }),
  );

  onCleanup(
    root,
    on(root, 'pointermove', (event: Event) => {
      const pointer = event as PointerEvent;
      if (pointer.pointerId !== pointerId) return;
      const next = valuesAt(pointer.clientX, pointer.clientY);
      trail.push({ x: next.x, y: next.y });
      if (trail.length > XY_TRAIL_LIMIT) trail.shift();
      setPosition(next.x, next.y);
      options.onChange(x, y);
    }),
  );

  const finish = (event: Event): void => {
    const pointer = event as PointerEvent;
    if (pointer.pointerId !== pointerId) return;
    pointerId = null;
    root.classList.remove('is-dragging');
    trail.length = 0;
    if (!latched) {
      setPosition(0.5, 0.5);
      options.onChange(x, y);
    } else {
      render();
    }
    options.onEnd?.(x, y);
  };

  onCleanup(root, on(root, 'pointerup', finish));
  onCleanup(root, on(root, 'pointercancel', finish));

  if (options.x !== undefined) {
    bindReactive(root, options.x, (value) => {
      if (pointerId === null) setPosition(value, y);
    });
  }
  if (options.y !== undefined) {
    bindReactive(root, options.y, (value) => {
      if (pointerId === null) setPosition(x, value);
    });
  }
  if (options.enabled !== undefined) {
    bindReactive(root, options.enabled, (value) => {
      root.classList.toggle('is-disabled', !value);
      xAxis.setAttribute('tabindex', value ? '0' : '-1');
      yAxis.setAttribute('tabindex', value ? '0' : '-1');
    });
  }

  onCleanup(root, () => surface.dispose());
  onCleanup(root, observeSize(surface.canvas, () => render()));

  surface.measure();
  render();

  return {
    ...baseHandle(root, (enabled) => {
      root.classList.toggle('is-disabled', !enabled);
      xAxis.setAttribute('tabindex', enabled ? '0' : '-1');
      yAxis.setAttribute('tabindex', enabled ? '0' : '-1');
    }),
    setPosition,
    setLabels,
    get position() {
      return { x, y };
    },
  };
}

// MARK: - Canvas helpers shared with the other component modules

/** `roundRect` is Safari 16+; this is the same shape everywhere. */
export function roundedRect(
  context: CanvasRenderingContext2D,
  x: number,
  y: number,
  width: number,
  height: number,
  radius: number,
): void {
  const r = Math.max(0, Math.min(radius, width / 2, height / 2));
  context.beginPath();
  context.moveTo(x + r, y);
  context.lineTo(x + width - r, y);
  context.arcTo(x + width, y, x + width, y + r, r);
  context.lineTo(x + width, y + height - r);
  context.arcTo(x + width, y + height, x + width - r, y + height, r);
  context.lineTo(x + r, y + height);
  context.arcTo(x, y + height, x, y + height - r, r);
  context.lineTo(x, y + r);
  context.arcTo(x, y, x + r, y, r);
  context.closePath();
}

let idCounter = 0;

/** Stable, collision-free element ids for `aria-describedby` / `aria-labelledby` wiring. */
export function nextId(): string {
  idCounter += 1;
  return String(idCounter);
}
